import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { RichTextView } from "@/components/ui/rich-text";
import { formatDateTime } from "@/lib/dates";
import { CircleNotch, ArrowCounterClockwise, FilePdf, FileXls, FileDoc, Eye, CaretDown, CaretRight } from "@phosphor-icons/react";

const ANY = "__any__";
const SECTION_LABELS = {
  overview: "Prospect Overview", contacts: "Contacts", intelligence: "Prospect Intelligence",
  activities: "Prospecting Activity", properties: "Properties / Locations", attachments: "Attachment Index",
};
const ALL_SECTIONS = Object.keys(SECTION_LABELS);
const OUTCOMES = ["Connected", "Left voicemail", "No answer", "Email sent", "Meeting scheduled",
  "Meeting held", "Proposal sent", "Not interested", "No outcome recorded"];
const EMPTY = { prospectId: "", sections: [], fields: {}, contactIds: [], propertyIds: [], activityFilters: {}, propertySort: "name" };

function SectionFields({ section, catalog, selected, onToggle }) {
  const [open, setOpen] = useState(false);
  const fields = catalog[section] || [];
  if (!fields.length) return null;
  const chosen = selected[section];
  return (
    <div className="mt-1 rounded-sm bg-[#FAFAFA] px-2 py-1">
      <button type="button" onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-1 text-[11px] font-semibold text-[#52525B] hover:text-[#032852]"
        data-testid={`fields-toggle-${section}`}>
        {open ? <CaretDown size={11} /> : <CaretRight size={11} />}
        Fields ({chosen?.length ? `${chosen.length} selected` : "all"})
      </button>
      {open && (
        <div className="mt-1 grid grid-cols-2 gap-x-2 gap-y-0.5 pb-1 sm:grid-cols-3">
          {fields.map((f) => (
            <label key={f.key} className="flex cursor-pointer items-center gap-1.5 text-[11px] text-[#3F3F46]">
              <Checkbox checked={!chosen || chosen.includes(f.key)}
                onCheckedChange={() => onToggle(section, f.key, fields.map((x) => x.key))}
                data-testid={`field-${section}-${f.key}`} className="h-3.5 w-3.5" />
              {f.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function PreviewTable({ data, testId }) {
  if (!data.rows.length) return <p className="py-3 text-xs text-[#6D6D70]">No records.</p>;
  return (
    <div className="max-h-[420px] overflow-auto rounded-sm border border-[#E4E4E7]" data-testid={testId}>
      <table className="w-full text-left text-xs">
        <thead className="sticky top-0 z-10 bg-[#032852]">
          <tr>{data.columns.map((c) => (
            <th key={c.key} className="whitespace-nowrap px-2.5 py-2 text-[10px] font-bold uppercase text-white">{c.label}</th>))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r, i) => (
            <tr key={r.id || i} className="border-b border-[#F4F4F5] align-top last:border-0 odd:bg-white even:bg-[#FAFAFA]">
              {data.columns.map((c) => (
                <td key={c.key} className="max-w-[260px] whitespace-pre-wrap px-2.5 py-1.5 text-[#3F3F46]">{String(r[c.key] ?? "")}</td>))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ProspectReportBuilder() {
  const { isAdmin } = useAuth();
  const [params, setParams] = useSearchParams();
  const [prospects, setProspects] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [refs, setRefs] = useState({ types: [], users: [] });
  const [cfg, setCfg] = useState(EMPTY);
  const [detail, setDetail] = useState(null); // selected prospect's contacts/properties
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null); // "run" | format
  const [ready, setReady] = useState(false);
  const saveTimer = useRef(null);

  // Load options, prospect list, saved prefs; then apply any shortcut params.
  useEffect(() => {
    let live = true;
    Promise.all([
      api.get("/reports/prospect/options"), api.get("/prospects"),
      api.get("/reference/activity-types"), api.get("/users/options"),
      api.get("/reports/prospect/prefs"),
    ]).then(([opt, pros, types, users, prefs]) => {
      if (!live) return;
      setCatalog(opt.data.fields);
      setProspects([...pros.data].sort((a, b) =>
        (a.organization_name || "").trim().toLowerCase().localeCompare((b.organization_name || "").trim().toLowerCase())));
      setRefs({ types: types.data, users: users.data });
      const saved = prefs.data && prefs.data.prospectId ? { ...EMPTY, ...prefs.data } : EMPTY;
      const qsProspect = params.get("prospect");
      const qsContact = params.get("contact");
      const qsSection = params.get("section");
      if (qsProspect) {
        const fresh = { ...EMPTY, prospectId: qsProspect };
        if (qsContact) { fresh.sections = ["overview", "contacts", "intelligence"]; fresh.contactIds = [qsContact]; }
        if (qsSection && ALL_SECTIONS.includes(qsSection)) {
          fresh.sections = [...new Set([...(fresh.sections || []), "overview", qsSection])];
        }
        setCfg(fresh);
        setParams({}, { replace: true });
      } else {
        setCfg(saved);
      }
      setReady(true);
    }).catch(() => toast.error("Report builder options could not be loaded."));
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the chosen prospect's contacts & properties for the pickers.
  useEffect(() => {
    if (!cfg.prospectId) { setDetail(null); return; }
    let live = true;
    api.get(`/prospects/${cfg.prospectId}`).then((r) => { if (live) setDetail(r.data); })
      .catch(() => { if (live) setDetail(null); });
    return () => { live = false; };
  }, [cfg.prospectId]);

  // Persist configuration per user (debounced) until Reset is pressed.
  useEffect(() => {
    if (!ready) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put("/reports/prospect/prefs", cfg.prospectId ? cfg : {}).catch(() => {});
    }, 800);
    return () => clearTimeout(saveTimer.current);
  }, [cfg, ready]);

  const upd = (patch) => setCfg((s) => ({ ...s, ...patch }));
  const toggleSection = (s) => upd({
    sections: cfg.sections.includes(s) ? cfg.sections.filter((x) => x !== s) : [...cfg.sections, s] });
  const allSelected = ALL_SECTIONS.every((s) => cfg.sections.includes(s));
  const toggleField = (section, key, allKeys) => {
    const cur = cfg.fields[section] || allKeys;
    const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
    upd({ fields: { ...cfg.fields, [section]: next } });
  };
  const toggleId = (listKey, id) => upd({
    [listKey]: cfg[listKey].includes(id) ? cfg[listKey].filter((x) => x !== id) : [...cfg[listKey], id] });
  const setAf = (k) => (v) => upd({
    activityFilters: { ...cfg.activityFilters, [k]: v?.target ? v.target.value : (v === ANY ? "" : v) } });

  const body = () => ({
    prospect_id: cfg.prospectId, sections: cfg.sections,
    fields: cfg.fields, contact_ids: cfg.contactIds, property_ids: cfg.propertyIds,
    activity_filters: Object.fromEntries(Object.entries(cfg.activityFilters).filter(([, v]) => v)),
    property_sort: cfg.propertySort,
  });

  const generate = async () => {
    if (!cfg.prospectId) { toast.error("Select a prospect organization first."); return; }
    if (!cfg.sections.length) { toast.error("Select at least one report section."); return; }
    setBusy("run");
    try {
      const { data } = await api.post("/reports/prospect/run", body());
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const exportAs = async (format) => {
    setBusy(format);
    try {
      const { data: exp } = await api.post("/reports/prospect/export", { ...body(), format });
      toast.success(`${format.toUpperCase()} export ready`);
      const a = document.createElement("a");
      a.href = `${api.defaults.baseURL}/reports/exports/${exp.id}/download`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const reset = () => {
    setCfg(EMPTY);
    setPreview(null);
    api.put("/reports/prospect/prefs", {}).catch(() => {});
    toast.success("Report builder reset");
  };

  const af = cfg.activityFilters;
  return (
    <div className="mt-5 rounded-sm border border-[#E4E4E7] bg-white" data-testid="prospect-report-builder">
      <div className="flex flex-wrap items-start justify-between gap-2 border-b border-[#F4F4F5] px-4 py-3">
        <div>
          <h2 className="font-heading text-lg font-black text-[#032852]">Prospect Report Builder</h2>
          <p className="text-xs text-[#6D6D70]">Prepare for meetings, evaluate accounts and export property data for proposals and pricing.</p>
        </div>
        <Button variant="outline" size="sm" onClick={reset}
          className="rounded-sm border-[#DC2626]/40 text-xs font-bold text-[#B91C1C] hover:bg-[#DC2626]/5"
          data-testid="report-reset-btn">
          <ArrowCounterClockwise size={14} /> Reset
        </Button>
      </div>

      <div className="grid gap-4 px-4 py-4 lg:grid-cols-[280px_1fr]">
        {/* Configuration column */}
        <div className="space-y-3">
          <div>
            <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-[#52525B]">1 — Report source</p>
            <p className="rounded-sm bg-[#032852]/5 px-2.5 py-1.5 text-xs font-semibold text-[#032852]">Prospects</p>
          </div>
          <div>
            <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-[#52525B]">2 — Prospect organization</p>
            <Select value={cfg.prospectId || ANY}
              onValueChange={(v) => { upd({ prospectId: v === ANY ? "" : v, contactIds: [], propertyIds: [] }); setPreview(null); }}>
              <SelectTrigger className="rounded-sm" data-testid="report-prospect-select"><SelectValue placeholder="Select a prospect…" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value={ANY}>Select a prospect…</SelectItem>
                {prospects.map((p) => <SelectItem key={p.id} value={p.id}>{p.organization_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-[#52525B]">3 — Report sections</p>
            <label className="flex cursor-pointer items-center gap-2 rounded-sm px-1 py-1 text-xs font-bold text-[#032852]">
              <Checkbox checked={allSelected}
                onCheckedChange={() => upd({ sections: allSelected ? [] : [...ALL_SECTIONS] })}
                data-testid="section-all" /> All of the Above
            </label>
            {ALL_SECTIONS.map((s) => (
              <div key={s}>
                <label className="flex cursor-pointer items-center gap-2 rounded-sm px-1 py-1 text-xs font-semibold text-[#3F3F46] hover:bg-[#FAFAFA]">
                  <Checkbox checked={cfg.sections.includes(s)} onCheckedChange={() => toggleSection(s)}
                    data-testid={`section-${s}`} /> {SECTION_LABELS[s]}
                </label>
                {cfg.sections.includes(s) && s !== "intelligence" && (
                  <SectionFields section={s} catalog={catalog} selected={cfg.fields} onToggle={toggleField} />
                )}
                {cfg.sections.includes(s) && s === "contacts" && detail && (detail.contacts || []).length > 0 && (
                  <div className="mt-1 max-h-32 space-y-0.5 overflow-y-auto rounded-sm bg-[#FAFAFA] px-2 py-1.5">
                    <p className="text-[10px] font-bold uppercase text-[#9CA3AF]">Contacts (none checked = all)</p>
                    {detail.contacts.map((c) => (
                      <label key={c.id} className="flex cursor-pointer items-center gap-1.5 text-[11px] text-[#3F3F46]">
                        <Checkbox checked={cfg.contactIds.includes(c.id)} onCheckedChange={() => toggleId("contactIds", c.id)}
                          className="h-3.5 w-3.5" data-testid={`pick-contact-${c.id}`} />
                        {c.first_name} {c.last_name}
                      </label>
                    ))}
                  </div>
                )}
                {cfg.sections.includes(s) && s === "properties" && detail && (detail.properties || []).length > 0 && (
                  <div className="mt-1 space-y-1 rounded-sm bg-[#FAFAFA] px-2 py-1.5">
                    <div className="max-h-32 space-y-0.5 overflow-y-auto">
                      <p className="text-[10px] font-bold uppercase text-[#9CA3AF]">Properties (none checked = all)</p>
                      {detail.properties.map((p) => (
                        <label key={p.id} className="flex cursor-pointer items-center gap-1.5 text-[11px] text-[#3F3F46]">
                          <Checkbox checked={cfg.propertyIds.includes(p.id)} onCheckedChange={() => toggleId("propertyIds", p.id)}
                            className="h-3.5 w-3.5" data-testid={`pick-property-${p.id}`} />
                          {p.name}
                        </label>
                      ))}
                    </div>
                    <Select value={cfg.propertySort} onValueChange={(v) => upd({ propertySort: v })}>
                      <SelectTrigger className="h-7 rounded-sm text-[11px]" data-testid="property-sort-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[["name", "Sort: Property name"], ["city", "Sort: City"], ["state", "Sort: State"],
                          ["zip_code", "Sort: ZIP"], ["location_number", "Sort: Location #"]].map(([v, l]) => (
                          <SelectItem key={v} value={v}>{l}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {cfg.sections.includes(s) && s === "activities" && (
                  <div className="mt-1 space-y-1.5 rounded-sm bg-[#FAFAFA] px-2 py-1.5">
                    <p className="text-[10px] font-bold uppercase text-[#9CA3AF]">Activity filters</p>
                    <div className="flex gap-1.5">
                      <DatePicker value={af.date_from || ""} onChange={setAf("date_from")} placeholder="From" className="h-7 flex-1 text-[11px]" testId="af-date-from" />
                      <DatePicker value={af.date_to || ""} onChange={setAf("date_to")} placeholder="To" className="h-7 flex-1 text-[11px]" testId="af-date-to" />
                    </div>
                    <Select value={af.activity_type_id || ANY} onValueChange={setAf("activity_type_id")}>
                      <SelectTrigger className="h-7 rounded-sm text-[11px]" data-testid="af-type"><SelectValue placeholder="All types" /></SelectTrigger>
                      <SelectContent><SelectItem value={ANY}>All types</SelectItem>
                        {refs.types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={af.outcome || ANY} onValueChange={setAf("outcome")}>
                      <SelectTrigger className="h-7 rounded-sm text-[11px]" data-testid="af-outcome"><SelectValue placeholder="All outcomes" /></SelectTrigger>
                      <SelectContent><SelectItem value={ANY}>All outcomes</SelectItem>
                        {OUTCOMES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                    {isAdmin && (
                      <Select value={af.owner_id || ANY} onValueChange={setAf("owner_id")}>
                        <SelectTrigger className="h-7 rounded-sm text-[11px]" data-testid="af-owner"><SelectValue placeholder="All salespeople" /></SelectTrigger>
                        <SelectContent><SelectItem value={ANY}>All salespeople</SelectItem>
                          {refs.users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                    <Select value={af.status || ANY} onValueChange={setAf("status")}>
                      <SelectTrigger className="h-7 rounded-sm text-[11px]" data-testid="af-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
                      <SelectContent><SelectItem value={ANY}>All statuses</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="upcoming">Upcoming</SelectItem>
                        <SelectItem value="overdue">Overdue</SelectItem></SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            ))}
          </div>
          <Button onClick={generate} disabled={busy === "run"}
            className="w-full rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]"
            data-testid="generate-preview-btn">
            {busy === "run" ? <CircleNotch size={16} className="animate-spin" /> : <><Eye size={16} /> Generate Preview</>}
          </Button>
        </div>

        {/* Preview column */}
        <div className="min-w-0">
          {!preview ? (
            <div className="flex h-full min-h-[200px] items-center justify-center rounded-sm border border-dashed border-[#E4E4E7] text-center"
              data-testid="report-preview-empty">
              <p className="px-6 text-xs text-[#6D6D70]">Select a prospect and sections, then Generate Preview to review the report here — no download required.</p>
            </div>
          ) : (
            <div data-testid="report-preview">
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-sm bg-[#032852] px-4 py-3">
                <div>
                  <p className="font-heading text-base font-black text-white" data-testid="preview-prospect-name">
                    Prospect Report — {preview.meta.prospect_name}</p>
                  <p className="text-[11px] text-white/70">
                    Generated {formatDateTime(preview.meta.generated_at)} by {preview.meta.generated_by}</p>
                </div>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="secondary" className="h-8 rounded-sm text-xs font-bold" disabled={!!busy}
                    onClick={() => exportAs("pdf")} data-testid="prospect-export-pdf">
                    {busy === "pdf" ? <CircleNotch size={14} className="animate-spin" /> : <FilePdf size={14} />} PDF</Button>
                  <Button size="sm" variant="secondary" className="h-8 rounded-sm text-xs font-bold" disabled={!!busy}
                    onClick={() => exportAs("xlsx")} data-testid="prospect-export-xlsx">
                    {busy === "xlsx" ? <CircleNotch size={14} className="animate-spin" /> : <FileXls size={14} />} Excel</Button>
                  <Button size="sm" variant="secondary" className="h-8 rounded-sm text-xs font-bold" disabled={!!busy}
                    onClick={() => exportAs("docx")} data-testid="prospect-export-docx">
                    {busy === "docx" ? <CircleNotch size={14} className="animate-spin" /> : <FileDoc size={14} />} Word</Button>
                </div>
              </div>
              <div className="mt-3 space-y-5">
                {preview.sections.overview && (
                  <section data-testid="preview-overview">
                    <h3 className="mb-2 border-b border-[#E4E4E7] pb-1 font-heading text-sm font-black uppercase tracking-wide text-[#032852]">Prospect Overview</h3>
                    <div className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
                      {preview.sections.overview.fields.map((f) => (
                        <p key={f.key} className="flex gap-2 text-xs">
                          <span className="w-40 shrink-0 text-[#9CA3AF]">{f.label}</span>
                          <span className="min-w-0 whitespace-pre-wrap break-words font-medium text-[#3F3F46]">{String(f.value ?? "")}</span>
                        </p>
                      ))}
                    </div>
                  </section>
                )}
                {preview.sections.intelligence && (
                  <section data-testid="preview-intelligence">
                    <h3 className="mb-2 border-b border-[#E4E4E7] pb-1 font-heading text-sm font-black uppercase tracking-wide text-[#032852]">Prospect Intelligence</h3>
                    {preview.sections.intelligence.prospect_html
                      ? <RichTextView html={preview.sections.intelligence.prospect_html} testId="preview-intel-prospect" />
                      : <p className="text-xs text-[#6D6D70]">No prospect intelligence recorded.</p>}
                    {preview.sections.intelligence.contacts.map((c) => (
                      <div key={c.name} className="mt-3">
                        <p className="text-xs font-bold text-[#032852]">Contact Intelligence — {c.name}</p>
                        <RichTextView html={c.html} />
                      </div>
                    ))}
                  </section>
                )}
                {["contacts", "activities", "properties", "attachments"].map((s) => preview.sections[s] && (
                  <section key={s} data-testid={`preview-${s}`}>
                    <h3 className="mb-2 border-b border-[#E4E4E7] pb-1 font-heading text-sm font-black uppercase tracking-wide text-[#032852]">
                      {SECTION_LABELS[s]} ({preview.sections[s].rows.length})</h3>
                    <PreviewTable data={preview.sections[s]} testId={`preview-table-${s}`} />
                  </section>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
